import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, RefreshCw, Home, HelpCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const errorMessages = {
  access_denied: {
    title: 'Access Denied',
    description: 'You denied access to the application. Please try again and authorize the application to continue.',
    suggestions: ['Try logging in again', 'Make sure to click "Allow" when prompted', 'Check if your account has the necessary permissions']
  },
  invalid_client_id: {
    title: 'Invalid Client Configuration',
    description: 'There is an issue with the application configuration. Please contact support.',
    suggestions: ['Contact the system administrator', 'Verify the OAuth application is properly configured']
  },
  csrf_mismatch: {
    title: 'Security Error',
    description: 'A security check failed. Please try logging in again.',
    suggestions: ['Clear your browser cache and cookies', 'Try logging in again', 'Disable any browser extensions that might interfere']
  },
  database: {
    title: 'Database Error',
    description: 'Failed to create or update your user account. The database might be temporarily unavailable.',
    suggestions: ['Try logging in again in a few moments', 'Contact the administrator if the issue persists']
  },
  token_exchange: {
    title: 'Token Exchange Failed',
    description: 'Failed to exchange authorization code with the OAuth provider.',
    suggestions: ['Try logging in again', 'Check if the OAuth provider is available']
  },
  token_parse: {
    title: 'Invalid Token',
    description: 'The OAuth provider returned an invalid or empty token response.',
    suggestions: ['Try logging in again', 'Contact the administrator']
  },
  userinfo: {
    title: 'User Info Error',
    description: 'Failed to retrieve your user information from the OAuth provider.',
    suggestions: ['Try logging in again', 'Make sure your account has an email address set']
  },
  invalid_state: {
    title: 'Session Expired',
    description: 'Your OAuth session expired or the state token was invalid. This often happens if you took too long to authenticate.',
    suggestions: ['Try logging in again', 'Make sure cookies are enabled', 'Avoid using the browser back button during login']
  },
  jwt: {
    title: 'Token Creation Failed',
    description: 'Failed to create an authentication token after successful OAuth login.',
    suggestions: ['Try logging in again', 'Contact the administrator']
  },
  auth_failed: {
    title: 'Authentication Failed',
    description: 'Unable to complete the authentication process. Please try again.',
    suggestions: ['Check your internet connection', 'Try logging in again', 'Clear browser cache and try again']
  },
  no_token: {
    title: 'Missing Authentication Token',
    description: 'No authentication token was received from OAuth provider.',
    suggestions: ['Try the login process again', 'Check if popups are blocked', 'Ensure JavaScript is enabled']
  },
  no_code: {
    title: 'Missing Authorization Code',
    description: 'No authorization code was received from OAuth provider.',
    suggestions: ['Try the login process again', 'Check your network connection', 'Contact support if the issue persists']
  },
  no_csrf_token: {
    title: 'Session Expired',
    description: 'Your session expired during authentication. Please try again.',
    suggestions: ['Try logging in again', 'Make sure cookies are enabled', 'Clear browser cache and try again']
  }
};

function AuthError() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const errorType = searchParams.get('error') || 'auth_failed';
  const errorMessage = searchParams.get('message');
  const errorInfo = errorMessages[errorType] || errorMessages.auth_failed;

  return (
    <>
      <Helmet><title>Authentication Error - Rspamd Email Scanner</title></Helmet>
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 text-center">
            <AlertCircle className="w-20 h-20 text-red-500 mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-1">{errorInfo.title}</h1>
            <p className="text-gray-500 mb-6">Authentication Error</p>

            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded text-left mb-6">
              <p>{errorInfo.description}</p>
              {errorMessage && <p className="mt-2 text-sm text-red-600">Details: {errorMessage}</p>}
              <p className="mt-1 text-xs text-red-400">Error code: {errorType}</p>
            </div>

            <div className="text-left mb-6">
              <h3 className="font-semibold flex items-center gap-2 mb-2">
                <HelpCircle className="w-5 h-5" /> What you can try:
              </h3>
              <ul className="space-y-1 text-sm text-gray-600 list-disc list-inside">
                {errorInfo.suggestions.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <button onClick={() => navigate('/login')} className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition">
                <RefreshCw className="w-5 h-5" /> Try Again
              </button>
              <button onClick={() => navigate('/')} className="flex items-center gap-2 border-2 border-gray-300 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition">
                <Home className="w-5 h-5" /> Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AuthError;
